/**
 * What a role costs, on screen.
 *
 * One component for both ends of the workflow — the propose-a-role form on the Org Planning
 * Overview and the card on Role Approvals — so a proposer and an approver are shown the same two
 * figures in the same shape. Everything it renders is computed on the proxy
 * (`salaryBandMatch.js`); this does no arithmetic, deliberately, because a second implementation of
 * the proration would eventually disagree with the one in the Slack announcement.
 *
 * ⚠️ The "unavailable" states are rendered, never hidden. A silently missing salary reads as a free
 * role; `UNAVAILABLE_LABELS` says which of the four reasons it is, and three of the four are fixed
 * by a row in the salary bands base rather than by anyone touching this code. They are AMBER rather
 * than the panel's neutral grey, matching how the rest of this app marks something to act on.
 *
 * ⚠️ `RADIUS` comes from the org-planning chrome, which is a `views/` module — the wrong direction
 * for something under `components/`, and correct only because both consumers are org-planning
 * pages (`approvalUi.tsx` already re-exports the same constant). A third consumer outside those
 * pages is the signal to move this file beside its callers or take the radius as a prop.
 */
import {
  type RoleCompensation,
  UNAVAILABLE_LABELS,
  UNAVAILABLE_SHORT,
  bandSalaryLabel,
  costCaption,
  formatEuro,
  hasSalary,
} from '../types/compensation';
import { RADIUS } from '../views/orgPlanningChrome';

/**
 * Two figures side by side, for the proposal form and the approval card.
 *
 * ⚠️ The `aria-live` wrapper renders even with nothing to show. On the proposal form the figures
 * arrive after a `<select>` changes, which is a change somewhere else on the page with nothing to
 * announce it — and a live region added at the same moment as its content does not announce that
 * content. It has to already be in the DOM, so this returns an empty region rather than `null`.
 */
export function CompensationSummary({
  compensation,
  busy = false,
  className = '',
}: {
  compensation: RoleCompensation | null | undefined;
  /** True while the first costing is still in flight — the region is marked busy so a screen
   * reader does not announce the empty state on its way to the real one. */
  busy?: boolean;
  className?: string;
}) {
  return (
    <div aria-live="polite" aria-busy={busy || undefined} className={compensation ? className : undefined}>
      {compensation ? <Panel compensation={compensation} /> : null}
    </div>
  );
}

function Panel({ compensation }: { compensation: RoleCompensation }) {
  if (!hasSalary(compensation)) {
    return (
      <div className={`${RADIUS} border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900`}>
        {UNAVAILABLE_LABELS[compensation.unavailable ?? 'no_band']}
      </div>
    );
  }

  return (
    // White on a zinc-50 card, with a stronger border than the rationale quote beside it: this is
    // the figure somebody commits money on, and it should not read as one more grey box.
    <dl className={`${RADIUS} grid gap-3 border border-zinc-300 bg-white px-3 py-2 sm:grid-cols-2`}>
      <Figure label="Salary band (average)" value={bandSalaryLabel(compensation)} />
      <Figure
        label={`Fully loaded cost until 31 Dec ${compensation.year}`}
        value={formatEuro(compensation.costUntilYearEnd)}
        caption={costCaption(compensation)}
      />
    </dl>
  );
}

/**
 * The label sits ABOVE the value, the same rule the TA Cockpit's KPI strips record: an inline
 * "label value" row wraps mid-phrase at card width, and these labels are long.
 *
 * `<dt>`/`<dd>` rather than two `<p>`s so the pair is navigable as a unit. The caption is `zinc-500`
 * and not `zinc-400` — it is the only place the proration is explained, and 400 on white is under
 * the contrast floor, which `approvalUi.tsx` already records for its "Waiting" pill.
 */
function Figure({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</dt>
      <dd className="text-sm font-semibold tabular-nums text-zinc-900">{value}</dd>
      {caption && <dd className="text-[11px] text-zinc-500">{caption}</dd>}
    </div>
  );
}

/**
 * One line, for a row in a list — the bulk proposal step, where twenty-five of these stack up and a
 * two-figure panel each would bury the dates they exist to annotate.
 *
 * Uses `UNAVAILABLE_SHORT`: the full sentence names the fix, which belongs in the panel and not
 * repeated twenty-five times down a narrow column.
 */
export function CompensationLine({ compensation }: { compensation: RoleCompensation | null | undefined }) {
  if (!compensation) return null;
  if (!hasSalary(compensation)) {
    return (
      <span className="text-[11px] text-amber-700">
        {UNAVAILABLE_SHORT[compensation.unavailable ?? 'bands_unavailable']}
      </span>
    );
  }
  return (
    <span className="text-[11px] tabular-nums text-zinc-500">
      {bandSalaryLabel(compensation)}
      <span className="text-zinc-300"> · </span>
      {formatEuro(compensation.costUntilYearEnd)} until 31 Dec {compensation.year}
    </span>
  );
}
